import { useContext } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import useActionCable from "../shared/useActionCable";
import apiRequest from "../../utils/apiRequest";

// Lazy TTL refresh hook. Stale-while-revalidate:
//   - serves cached `integration.api_data` immediately
//   - POSTs /integrations/:id/refresh_if_stale to enqueue a BuildJob (if stale)
//   - subscribes to IntegrationRefreshChannel scoped to the account; BuildJob
//     broadcasts integration.to_prop on success and the hook patches contextData
//   - tracks which integrations are mid-refresh so dependent controls can show
//     a loader (CFSelect isLoading)
//
// Used across the variant builder (action edit, condition edit, field map).
// Reusable for any future surface — call refreshIntegration(id) on mount and
// pass isIntegrationRefreshing(id) into the dependent dropdown's isLoading.

// Module-level guard so simultaneous mounts don't double-fire the POST before
// context state catches up. Stores a start timestamp per integration and is
// time-bounded so a lost/never-arriving ActionCable broadcast (failed BuildJob,
// unmount mid-flight) doesn't block future refreshes for the rest of the SPA session.
const inFlight = new Map();
const INFLIGHT_TTL = 30000;

const useIntegrationRefresh = () => {
  const contextTuple = useContext(VariantContextData);
  const hasContext = Array.isArray(contextTuple);
  const contextData = hasContext ? contextTuple[0] || {} : {};
  const setContextData = hasContext ? contextTuple[2] : null;
  const account = contextData.objects && contextData.objects.account;
  const accountId = account && account.id;
  const website = contextData.objects && contextData.objects.website;
  const websiteId = website && website.id;

  const isIntegrationRefreshing = (integrationId) => {
    if (!integrationId) return false;
    const map = contextData.refreshing_integrations || {};
    return Boolean(map[String(integrationId)]);
  };

  const setRefreshing = (integrationId, value) => {
    if (!integrationId || !setContextData) return;
    const key = String(integrationId);
    setContextData((prev) => {
      const next = { ...prev };
      const map = { ...(prev.refreshing_integrations || {}) };
      if (value) map[key] = true;
      else delete map[key];
      next.refreshing_integrations = map;
      return next;
    });
  };

  const patchIntegration = (integration) => {
    if (!integration || !integration.id || !setContextData) return;
    setContextData((prev) => {
      const existing = (prev.objects.website.integrations || {})[
        integration.id
      ];
      if (!existing) return prev;

      const next = { ...prev };
      next.objects = { ...prev.objects };
      next.objects.website = { ...prev.objects.website };
      next.objects.website.integrations = { ...prev.objects.website.integrations };
      next.objects.website.integrations[integration.id] = {
        ...existing,
        api_data: integration.api_data,
        status: typeof integration.status !== "undefined" ? integration.status : existing.status,
        field_map: typeof integration.field_map !== "undefined" ? integration.field_map : existing.field_map,
        action_types: integration.action_types || existing.action_types,
        condition_types: integration.condition_types || existing.condition_types,
      };
      return next;
    });
  };

  useActionCable(
    accountId ? "IntegrationRefreshChannel" : null,
    accountId ? { id: accountId } : {},
    {
      onReceived: (data) => {
        if (!data || !data.integration_id) return;
        if (data.integration) patchIntegration(data.integration);
        setRefreshing(data.integration_id, false);
        inFlight.delete(String(data.integration_id));
      },
    }
  );

  const refreshIntegration = async (integrationId) => {
    if (!integrationId || !websiteId) return;
    const key = String(integrationId);
    const startedAt = inFlight.get(key);
    if (startedAt && Date.now() - startedAt < INFLIGHT_TTL) return;
    inFlight.set(key, Date.now());

    const resp = await apiRequest(
      "POST",
      `/websites/${websiteId}/integrations/${integrationId}/refresh_if_stale`,
      {}
    );
    const data = resp && resp.json;
    if (data && data.refreshed && data.job_id) {
      setRefreshing(integrationId, true);
    } else {
      inFlight.delete(key);
    }
  };

  return { refreshIntegration, isIntegrationRefreshing };
};

export default useIntegrationRefresh;
